import { fmt } from "../../lib/api";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const COLORS = ["#2F554A", "#C98F4B", "#4A7386", "#AA6B3C", "#728570", "#7C5F8A"];

const KAT_LABELS = {
  k1: "Kategori 1 · Doğrudan",
  k2: "Kategori 2 · Elektrik",
  k3: "Kategori 3 · Ulaşım",
  k4: "Kategori 4 · Tüketim",
  k5: "Kategori 5 · Ürün",
  k6: "Kategori 6 · Diğer",
};

export default function Summary({ results }) {
  if (!results) return <div className="text-muted-foreground">Hesaplama bekleniyor…</div>;

  const { ozet } = results;
  const pieData = Object.keys(KAT_LABELS).map((k, i) => ({
    name: KAT_LABELS[k],
    value: results[k]?.total || 0,
    fill: COLORS[i],
    yuzde: ozet.dagilim_yuzde[k] || 0,
  })).filter((d) => d.value > 0);

  const scopeData = [
    { name: "Kapsam 1 (Doğrudan)", value: ozet.kapsam1, fill: "#2F554A" },
    { name: "Kapsam 2 (Elektrik)", value: ozet.kapsam2, fill: "#C98F4B" },
    { name: "Kapsam 3 (Diğer Dolaylı)", value: ozet.kapsam3, fill: "#4A7386" },
  ];

  return (
    <div className="space-y-8 animate-fadeInUp">
      {/* Büyük toplam */}
      <div className="relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/5 via-card to-card p-8 md:p-12">
        <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
          Yıllık Toplam Sera Gazı Emisyonu
        </div>
        <div className="font-display text-6xl md:text-7xl font-semibold text-primary leading-none mb-2 tabular-nums">
          {fmt(ozet.total, 2)}
        </div>
        <div className="text-2xl text-muted-foreground">ton CO₂ eşdeğeri</div>
        {ozet.oda_gece > 0 && (
          <div className="mt-6 pt-6 border-t border-border/70 flex flex-wrap gap-8">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Toplam Oda/Gece</div>
              <div className="font-display text-2xl font-semibold">{fmt(ozet.oda_gece, 0)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Oda Başına</div>
              <div className="font-display text-2xl font-semibold">{fmt(ozet.kisi_basina, 4)} <span className="text-sm text-muted-foreground">tCO₂e</span></div>
            </div>
          </div>
        )}
      </div>

      {/* Kategori dağılımı */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="font-display text-xl font-semibold mb-4">Kategori Dağılımı</h3>
          {pieData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
              Hesaplama için veri girin.
            </div>
          ) : (
            <>
              <div className="h-64">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" innerRadius={55} outerRadius={90} paddingAngle={2}>
                      {pieData.map((e, i) => <Cell key={i} fill={e.fill} stroke="none" />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${fmt(v, 3)} tCO₂e`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 space-y-2">
                {pieData.map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="size-2.5 rounded-full" style={{ background: d.fill }} />
                      <span>{d.name}</span>
                    </div>
                    <div className="text-muted-foreground tabular-nums">
                      {fmt(d.value, 3)} <span className="text-xs">· %{fmt(d.yuzde, 2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="font-display text-xl font-semibold mb-4">Kapsam Analizi</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={scopeData} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={170} />
                <Tooltip formatter={(v) => `${fmt(v, 3)} tCO₂e`} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {scopeData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Özet tablo */}
      <div className="rounded-2xl border border-border bg-card p-6">
        <h3 className="font-display text-xl font-semibold mb-4">Özet Tablo</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="py-2 pr-4 font-medium">Kategori</th>
                <th className="py-2 px-4 font-medium text-right">Emisyon (tCO₂e)</th>
                <th className="py-2 pl-4 font-medium text-right">Dağılım</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(KAT_LABELS).map((k) => {
                const v = results[k]?.total || 0;
                const p = ozet.dagilim_yuzde[k] || 0;
                return (
                  <tr key={k} className="border-b border-border/50">
                    <td className="py-3 pr-4">{KAT_LABELS[k]}</td>
                    <td className="py-3 px-4 text-right tabular-nums">{fmt(v, 3)}</td>
                    <td className="py-3 pl-4 text-right tabular-nums text-muted-foreground">%{fmt(p, 2)}</td>
                  </tr>
                );
              })}
              <tr className="font-semibold text-primary">
                <td className="py-3 pr-4">TOPLAM</td>
                <td className="py-3 px-4 text-right tabular-nums">{fmt(ozet.total, 3)}</td>
                <td className="py-3 pl-4 text-right">%100</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
